import { useRequest } from 'ahooks';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { take_daygold_v2 } from '@/api/hall';
import { implog } from '@/api/implog';
import dailyGold from '@/assets/modal/icon_daily_gold.webp';
import expired from '@/assets/promotion/daily_expired.webp';
// import received from '@/assets/promotion/daily_received.svg';
import Button from '@/components/button';
import CountDown from '@/components/count-down';
import { IconTime } from '@/components/icons/icons';
import BlurryLoadingImage from '@/components/progressive-img';
import { currentDailyInfoAtom, dailyConfigAtom, useDailyModalStore } from '@/jotai/daygold';
import { emitterAtom } from '@/jotai/emitter';
import RecommendGames from '@/modules/RecommendGames';
import { modalStore } from '@/stores/modal';
import globalStore from '@/stores/store';

import styles from './styles.module.less';

const DailyItem = ({
  size,
  type,
  day,
  amount,
  onClick
}: {
  day: any;
  amount: number;
  size: 'big' | 'small';
  type?: 'disabled' | 'expired' | 'received' | '';
  onClick: () => void;
}) => {
  const content = useMemo(() => {
    if (size === 'big') {
      return (
        <>
          <BlurryLoadingImage loading="lazy" className={styles.gold} src={dailyGold} alt="" />
          <div className={styles.title}>DAY {day}</div>
        </>
      );
    }
    return (
      <>
        <div className={styles.title}>DAY {day}</div>
        <BlurryLoadingImage loading="lazy" className={styles.gold} src={dailyGold} alt="" />
      </>
    );
  }, [size]);
  return (
    <div
      onClick={() => {
        if (type) return;
        onClick();
      }}
      className={classNames(styles.item, {
        [styles['small-item']]: size === 'small',
        [styles['big-item']]: size === 'big',
        [styles['item-received']]: type === 'received' || type === 'expired'
      })}
    >
      {!type ? (
        <BlurryLoadingImage
          loading="lazy"
          className={styles['item-active']}
          src={(window?.imgPrefix || '') + '/img/modal/daily_item_bg.webp'}
        />
      ) : null}
      <div
        className={classNames(styles.mask, {
          [styles['mask-visible']]: type === 'received' || type === 'expired'
        })}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {type === 'expired' && <BlurryLoadingImage loading="lazy" className={styles.expired} src={expired} />}
        {type === 'received' && (
          <BlurryLoadingImage
            loading="lazy"
            className={styles.received}
            src={`${window?.imgPrefix || ''}/img/promotion/daily_received.svg`}
          />
        )}
      </div>
      {content}
      <div className={styles.itemtext}>$ {amount}</div>
    </div>
  );
};

const DailyModal = ({ from }) => {
  const [update] = useDailyModalStore();
  const dailyConfigList = useAtomValue(dailyConfigAtom);
  const currentConfig = useAtomValue(currentDailyInfoAtom);
  const emitter = useAtomValue(emitterAtom);

  const { next_take_timer = 0, is_need_pay } = currentConfig;

  const navigate = useNavigate();

  const runReceived = () => {
    implog('daily_modal_receive_click', { from: from || 'popup_list' });
    run();
  };

  const { run: run } = useRequest(take_daygold_v2, {
    manual: true,
    onSuccess: () => {
      update();
      globalStore.fetchBalance();
      emitter.emit({ type: 'withdraw_task_update' });
    }
  });
  const renderStatusText = () => {
    if (next_take_timer > 0) {
      if (next_take_timer > 60 * 60 * 22)
        return <div className={styles.text}>Continue receiving your bonus tomorrow at 10 p.m.</div>;
      return <div className={styles.text}>Continue receiving your bonus today at 10 p.m.</div>;
    }
    return null;
  };
  const renderBtn = () => {
    if (is_need_pay === 1) {
      return (
        <Button
          className={styles.btn}
          block
          onClick={() => {
            modalStore.closeTaskChain();
            implog('daily_modal_click', { from: from || '' });
            navigate('/recharge');
          }}
          type="success"
        >
          Claim Bonus
        </Button>
      );
    }
    if (next_take_timer > 0) {
      return (
        <Button disabled block>
          <div className={styles.graybtn}>
            <IconTime className={styles.time} />
            <CountDown
              time={next_take_timer}
              finish={() => {
                update();
              }}
            />
          </div>
        </Button>
      );
    }
    return (
      <Button onClick={runReceived} className={styles.btn} type="success">
        Claim Bonus
      </Button>
    );
  };

  useEffect(() => {
    implog('daily_modal_show', { from: from || '' });
  }, []);
  return (
    <div className={styles.container}>
      <div className={styles.box}>
        <div className={styles.head}></div>
        <div className={styles.main}>
          {/* <BlurryLoadingImage loading="lazy" src={icon_ring} className={styles.left_ring} />
          <BlurryLoadingImage loading="lazy" src={icon_ring} className={styles.right_ring} /> */}
          <div className={styles.itembox}>
            <DailyItem
              type={dailyConfigList[0]?.status}
              day={1}
              amount={dailyConfigList[0]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
            <DailyItem
              type={dailyConfigList[1]?.status}
              day={2}
              amount={dailyConfigList[1]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
            <DailyItem
              type={dailyConfigList[2]?.status}
              day={3}
              amount={dailyConfigList[2]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
          </div>
          <div className={styles.itembox}>
            <DailyItem
              type={dailyConfigList[3]?.status}
              day={4}
              amount={dailyConfigList[3]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
            <DailyItem
              type={dailyConfigList[4]?.status}
              day={5}
              amount={dailyConfigList[4]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
            <DailyItem
              type={dailyConfigList[5]?.status}
              day={6}
              amount={dailyConfigList[5]?.amount}
              size="small"
              onClick={runReceived}
            ></DailyItem>
          </div>
          <div>
            <DailyItem
              type={dailyConfigList[6]?.status}
              day={'7+'}
              amount={dailyConfigList[6]?.amount}
              size="big"
              onClick={runReceived}
            ></DailyItem>
          </div>
          <div className={styles.btnbox}>
            <div
              style={{
                padding: '0.2rem 0.54rem',
                width: '100%',
                height: '1.2rem'
              }}
            >
              {renderBtn()}
            </div>
            {renderStatusText()}
          </div>
        </div>
      </div>
      <RecommendGames />
    </div>
  );
};

export default DailyModal;
